import type { Highlight, ProductOffering } from './ConciergeServiceSection.types';
import type { QuickTranslate } from '@shared/core/i18n/useTranslation';

const highlights = (...items: ReadonlyArray<Highlight>): ReadonlyArray<Highlight> => items;

const getInvitationImageUrl = (pathSuffix: string) => `https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites${pathSuffix}?v=2`;

const getSaveTheDateImageUrl = (pathSuffix: string) => `https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites${pathSuffix}?v=2`;

const getVideoThumbnailUrl = (videoId: string) => `https://i.vimeocdn.com/video/${videoId}-d_1280x720?r=pad`;

type ProductOfferingWithoutIds = Omit<ProductOffering, 'id' | 'identifier'>;

/**
 * Creates product offerings from an object by adding `id` & `identifier` to the object
 * @param obj - The object to create product offerings from - `id` & `identifier` will be added to the object
 * @returns The object with `id` & `identifier` added to the object
 */
export function createProductOfferings<T extends Record<string, ProductOfferingWithoutIds>>(obj: T) {
  // `fromEntries` would be better but using `reduce` for better browser compatibility
  return Object.keys(obj).reduce(
    (acc, key) => {
      const typedKey = key as keyof T;
      acc[typedKey] = { ...obj[typedKey], id: typedKey, identifier: typedKey };
      return acc;
    },
    {} as {
      [K in keyof T]: ProductOfferingWithoutIds & { readonly id: K; readonly identifier: K };
    },
  );
}

const getSharedHighlights = (tq: QuickTranslate) => {
  return {
    sharedPersonalization: { text: tq`Personalization with Joy concierge design services` },
    sharedEnvelopeAddressing: { text: tq`Recipient and return address printing` },
    sharedNotIncluded: { text: tq`Photographed design suggestions such as ribbons, wax seals, string, etc are not included unless specifically stated` },
  };
};

/**
 * A wrapper around `createProductOfferings` that creates invitation offerings
 * This helps us to properly type `PriceConfig` in `src/apps/card/routes/Concierge/config.ts`
 * @param tq - The translation function
 * @returns The invitation offerings
 */
export const createInvitationOfferingsData = (tq: QuickTranslate) => {
  const { sharedEnvelopeAddressing, sharedNotIncluded, sharedPersonalization } = getSharedHighlights(tq);

  const invitationOfferings = createProductOfferings({
    together: {
      typeformId: 'P4EMDsiM',
      shortName: 'Together',
      longName: 'Together Wedding Invitation Suite',
      costPerHundredString: '$5.92',
      videoUrl: 'https://player.vimeo.com/video/947047579?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853442234-787d4b800726a285c5dd2edc49aaab297f6efaa18a921d09b01bb853f971a48e'),
      imageUrl: getInvitationImageUrl('/together-gatefold/together-gatefold-1-main.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Printed on quality premium paper` },
        { text: tq`Customize with your own choice of photo` },
        { text: tq`Wax seals are not included, but can easily be purchased externally` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Photo gatefold wedding invitation card, 10” x 7” on 380 GSM premium paper` },
        { text: tq`This bundle includes a delicate vellum bellyband, measuring 10.5" × 2" and 80 GSM, to wrap around your invitations for a finished look` },
        { text: tq`Free premium white envelopes included with each invitation` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    milkAndHoney: {
      typeformId: 'Zk4NeIgp',
      shortName: 'Milk and Honey',
      longName: 'Milk and Honey Wedding Invitation Suite',
      costPerHundredString: '$8.74',
      videoUrl: 'https://player.vimeo.com/video/947047142?badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853441650-1e9eab448236c13eb967d5aa9eb793b23c6f4b09754dc3e4739d10c4c05017d3'),
      imageUrl: getInvitationImageUrl('/milk-and-honey/milk-and-honey-1-main.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Earthy tone invitation package perfect for those wanting neutral colors` },
        { text: tq`This set includes a wedding invitation, a details card which has the RSVP details on the back` },
        { text: tq`A bellyband to keep it all together and to add a finished touch` },
        { text: tq`Almond color envelopes as seen in the video` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5” x 7”, on 300GSM cotton` },
        { text: tq`Wedding details card, 5” x 7” half arch on 300 GSM almond paper` },
        { text: tq`Bellyband 120 GSM matte` },
        { text: tq`Premium almond color envelopes with options to select to grey, or harvest` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    sage: {
      typeformId: 'xKGL5sOQ',
      shortName: 'Sage',
      longName: 'Sage Wedding Invitation Suite',
      costPerHundredString: '$11.28',
      videoUrl: 'https://player.vimeo.com/video/947047267?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853441651-c49e0f48614b2978086924dd41716baf10bdeced9b21d1a967e2be77542f22d1'),
      imageUrl: getInvitationImageUrl('/sage/sage-1-main.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Deep forest colors combined with elegant neutrals combine for a refined but natural look` },
        { text: tq`Each set comes with gold paper clips to keep all your cards together` },
        { text: tq`Includes premium almond color envelopes included` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5” x 7” arch on Forest Green 300GSM paper with white ink` },
        { text: tq`Details card, 4.2” x 5.5” curved edge on 300 GSM Almond Paper` },
        { text: tq`RSVP card, 4.2” x 2.1” land scape arch on 300GSM paper` },
        { text: tq`Premium Almond Envelope` },
        { text: tq`Gold color paper clip` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    ivory: {
      typeformId: 'S5YGPoLv',
      shortName: 'Ivory',
      longName: 'Ivory Wedding Invitation Suite',
      costPerHundredString: '$11.02',
      videoUrl: 'https://player.vimeo.com/video/947046859?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853441091-25ae32d4ca89fb7e08092400588fbbe28ee9a7d9df56b0fdc69171f18f7a60c6'),
      imageUrl: getInvitationImageUrl('/ivory/ivory-1-main.jpg'),
      descriptionHighlights: highlights(
        { text: tq`When it comes to adding all the wedding info we like to keep it simple and elegant as possible.` },
        { text: tq`This set includes arch shape invitations printed on beautiful cotton paper` },
        { text: tq`Matching double-sided information card to include all your wedding day finer details` },
        { text: tq`A small landscape arch shape RSVP card, with the option to add your wedding website QR code` },
        { text: tq`Free black envelopes included` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5” x 7” arch on premium 300 GSM cotten paper` },
        { text: tq`Details card,  4.2” x 5.5” rounded edge on 300 GSM almond paper` },
        { text: tq`RSVP card, 4.2” x 2.1” landscape arch on 270 GSM black paper with white ink` },
        { text: tq`Premium black envelopes with white ink` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    sofia: {
      typeformId: 'fUgrImQ7',
      shortName: 'Sofia',
      longName: 'Sofia Wedding Invitation Suite',
      costPerHundredString: '$8.10',
      videoUrl: 'https://player.vimeo.com/video/947047427?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853441762-3cc48466d6550ed05108cb8f662fbd8e8446469012b444360a6f7961d28f950f'),
      imageUrl: getInvitationImageUrl('/sofia/sofia-1-main.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Make a grand entrance into your special day with this ${'Sofia'} invitation set` },
        { text: tq`Printed on shimmery metallic paper for a subtle glow` },
        { text: tq`Matching arch shaped wedding information card that fits perfectly with the main invitation` },
        { text: tq`A small landscape arch RSVP card, with the option to add your wedding website QR code on` },
        { text: tq`Free Almond color envelopes included to match the invitation set tones` },
        { text: tq`Each set comes with gold paper clips to keep all your cards together` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5” x 7” arch on 300 GSM metallic paper` },
        { text: tq`Details card, 5.8” x 3.7” arch on 300 GSM metallic paper` },
        { text: tq`RSVP card, 4.2” x 2.1” landscape arch on 300 GSM metallic paper` },
        { text: tq`Premium almond color envelope` },
        { text: tq`Gold paper clips to keep all your cards together` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    wildestDream: {
      typeformId: 'W5fDCgXN',
      shortName: 'Wildest Dreams',
      longName: 'Wildest Dreams Wedding Invitation Suite',
      costPerHundredString: '$9.46',
      videoUrl: 'https://player.vimeo.com/video/947047706?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853442320-6f5b0b94f1e861bc7d5b8da0d7f23132d42880f26d2f5821f02ff6287c29c357'),
      imageUrl: getInvitationImageUrl('/wildest-dreams/wildest-dreams-suite-1-main.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Immerse yourself in the timeless allure of this ${'Wildest Dreams'} invitations set foil-printed and intertwined with delicate floral print bellyband.` },
        { text: tq`A matching information card` },
        { text: tq`Transparent vellum bellyband to keep it all together and to add a finished touch` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gold foil invitation card, 5”x 7“ on premium 380 GSM paper` },
        { text: tq`Details card, 4.2” x 5.5” on premium 380 GSM paper` },
        { text: tq`Vellum bellyband, 10.5" x 2"  80 GSM ` },
        { text: tq`Premium white envelope` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    harmonyGatefold: {
      typeformId: 'yUU7XCiA',
      shortName: 'Harmony Gatefold',
      longName: 'Harmony Gatefold Wedding Invitation Suite',
      costPerHundredString: '$5.28',
      videoUrl: 'https://player.vimeo.com/video/947044716?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853438016-64478fea48afc207f85486f7ad8248b0412f8a688d61e3e4c10d91036a8c4013'),
      imageUrl: getInvitationImageUrl('/harmony-gatefold/harmony-gatefold-1-main.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Harmony Gatefold'} invitations are a perfect way to send your guests all the info they need for the day.` },
        { text: tq`Printed on our Premium paper with high quality photo printing on the outside` },
        { text: tq`This all-in-one wedding invitation includes all your wedding day finer details, RSVP, and wedding timeline` },
        { text: tq`Harvest color envelopes included with each invitation` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold Wedding Invitation, 10” x 7” on premium 380 GSM paper` },
        { text: tq`Premium harvest color envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    harmony: {
      typeformId: 'epzfVJQL',
      shortName: 'Harmony',
      longName: 'Harmony Wedding Invitation Suite',
      costPerHundredString: '$11.28',
      videoUrl: 'https://player.vimeo.com/video/947046740?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853440971-50a8a1d4d9d5983e2ca0ee4b88d058f6e4633d9d99213e76dbe554008410e499'),
      imageUrl: getInvitationImageUrl('/harmony-suite/harmony-suite-1-main.jpg'),
      descriptionHighlights: highlights(
        {
          text: tq`The ${'Harmony'} set is a perfect balance between wedding neutrals and greenery. With stunning script fonts and quality print and paper, this layered invitation set is bound to set the tone for your big day.`,
        },
        { text: tq`Matching double-sided information card that is big enough for all your wedding details` },
        { text: tq`Small green name card that can be used as a guest name card to tie in a hint of green to this suit.` },
        { text: tq`Free Grey envelopes with each invitation set.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5”x7” arch on Premium 300 GSM Cotten Paper` },
        { text: tq`Details card,  4.2” x 5.5” half arch on 300 GSM cotton paper` },
        { text: tq`Guest name card,  3.1” x 3.7”` },
        { text: tq`Premium grey envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    empireNow: {
      typeformId: 'grahvYKC',
      shortName: 'Empire Now',
      longName: 'Empire Now Wedding Invitation Suite',
      costPerHundredString: '$3.77',
      videoUrl: 'https://player.vimeo.com/video/947044156?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853437219-35b71461829d0e6f7be76a171df3842831ec96090781b290714cc573a26e43c1'),
      imageUrl: getInvitationImageUrl('/empire-now/empire-now-gatefold.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Empire Now'} all-in-one Gatefold design is a combination of curvy die-cut and elegant typography` },
        { text: tq`Customize with your own photo on the inside ` },
        { text: tq`Printed on our Premium paper with high quality photo printing` },
        { text: tq`This all-in-one wedding invitation includes all your wedding day finer details and RSVP information` },
        { text: tq`Black envelopes included with each invitation` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold Wedding Invitation, 11.8” x 7” side wave on premium 380 GSM paper` },
        { text: tq`Premium black envelopes with white ink` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    byronGatefold: {
      typeformId: 'BTaf4We4',
      shortName: 'Byron Gatefold',
      longName: 'Byron Gatefold Wedding Invitation Suite',
      costPerHundredString: '$5.28',
      videoUrl: 'https://player.vimeo.com/video/947044716?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853438016-64478fea48afc207f85486f7ad8248b0412f8a688d61e3e4c10d91036a8c4013'),
      imageUrl: getInvitationImageUrl('/byron-gatefold/byron-gatefold-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`All your invitation, RSVP, and wedding details in one gatefold card` },
        { text: tq`Customize with your own photo on the inside` },
        { text: tq`Printed on our Premium paper with high quality photo printing` },
        { text: tq`This all-in-one wedding invitation includes all your wedding day finer details and RSVP information` },
        { text: tq`Free Almond color envelopes with each invitation` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold Wedding Invitation, 10” x 7” on premium 380 GSM paper` },
        { text: tq`Premium almond color envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    jardin: {
      typeformId: 'z2Hl0QzC',
      shortName: 'Jardin',
      longName: 'Jardin Wedding Invitation Suite',
      costPerHundredString: '$5.28',
      videoUrl: 'https://player.vimeo.com/video/947046975?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853441362-42f030c8a90dd23ddd39ced311850e1e069f336c0c1fb00f0bd88d70c72ea8c0'),
      imageUrl: getInvitationImageUrl('/jardin/jardin-1-main.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Jardin gatefold'} invitation is for those flower and nature lovers, sure to set the tone for your special day.` },
        { text: tq`Printed on our Premium paper with high quality print showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes all your wedding day finer details and RSVP information` },
        { text: tq`Free envelopes to match for each invitation with a choice of Forest Green or Peach colored envelopes ` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10” x 7” on 380GSM premium paper` },
        { text: tq`Premium envelopes in forest green or peach` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    celadon: {
      typeformId: 'QNVBTrkI',
      shortName: 'Celadon',
      longName: 'Celadon Wedding Invitation Suite',
      costPerHundredString: '$5.79',
      videoUrl: 'https://player.vimeo.com/video/947046975?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1853441362-42f030c8a90dd23ddd39ced311850e1e069f336c0c1fb00f0bd88d70c72ea8c0'),
      imageUrl: getInvitationImageUrl('/celadon/celadon-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Celadon'} all-in-one Gatefold design is a combination of curvy die-cut and elegant typography` },
        { text: tq`Printed in white ink on our rich and vibrant Forest Green paper stock` },
        { text: tq`This all-in-one wedding invitation includes all your wedding day finer details and RSVP information` },
        { text: tq`Almond envelopes included with each invitation.` },
        { text: tq`Each set comes with gold paper clips to keep all your cards together` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Printed in white ink on rich and vibrant Forest Green paper` },
        { text: tq`Die-cut Gatefold Wedding Invitation, 11.8” x 7” side wave on premium 300 GSM paper` },
        { text: tq`Premium almond color envelopes` },
        sharedEnvelopeAddressing,
        { text: tq`Gold color paper clip` },
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 10,
    },
    wildMeadow: {
      typeformId: 'vM3b3cnu',
      shortName: 'Wild Meadow',
      longName: 'Wild Meadow Wedding Invitation Suite',
      costPerHundredString: '$7.47',
      videoUrl: 'https://player.vimeo.com/video/1002121907?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1918423147-1c4cdcfd97be9311d442a0c54da8d65acee0db54f360560e92b9e91bea3e7373'),
      imageUrl: getInvitationImageUrl('/wild-meadow/wild-meadow.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Wild Meadow'} gatefold invitation is for those flower lovers and sure to set the tone for your special day.` },
        { text: tq`Printed on our Premium paper with high quality print showing all the vibrant colors this design has to offer ` },
        { text: tq`This all-in-one wedding invitation includes all your wedding day finer details and RSVP information` },
        { text: tq`Tied together with a kraft color bellyband ` },
        { text: tq` Almond envelopes included with each invitation` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10” x 7” on 380 GSM premium paper` },
        { text: tq`Kraft bellyband, 10.5” x 2” 80 GSM` },
        { text: tq`Premium almond color envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 5,
    },
    eden: {
      typeformId: 'y8PT7OPz',
      shortName: 'Eden',
      longName: 'Eden Wedding Invitation Suite',
      costPerHundredString: '$7.23',
      videoUrl: 'https://player.vimeo.com/video/1002122162?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1918423449-e2cd083a494ea391fea3cbe969774e9083d8f6219e74cb5050701112f10853df'),
      imageUrl: getInvitationImageUrl('/eden/eden.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Eden'} gatefold invitation is for those flower and nature lovers, sure to set the tone for your special day.` },
        { text: tq`Printed on our Premium paper with high quality print showing all the vibrant colors this design has to offer ` },
        { text: tq`This all-in-one wedding invitation includes space for all your wedding day detail, RSVP and wedding day timeline information.` },
        { text: tq`Almond color envelopes and envelope liners included with each invitation set (Please note: envelope liners do not come assembled.` },
        { text: tq`Ribbon and wax seals are not included. They can be purchased externally.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10” x 7” on 380 GSM premium paper` },
        { text: tq`Premium almond color envelopes` },
        { text: tq`Delicate Leaves envelope liners` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 2,
    },
    luminous: {
      typeformId: 'bzEBZM69',
      shortName: 'Luminous',
      longName: 'Luminous Wedding Invitation Suite',
      costPerHundredString: '$7.51',
      videoUrl: 'https://player.vimeo.com/video/1002122372?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1918423640-11686ae80aff6e9d9f2b0dbc78b8e3007543c0c0115f63decb19ef4ed02528bc'),
      imageUrl: getInvitationImageUrl('/luminous/luminous.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Immerse yourself in the timeless allure of this ${'Luminous'} invitation set foil-printed ` },
        { text: tq`Paired with a delicate transparent (vellum) info card. ` },
        { text: tq`Almond color envelopes included with each invitation set.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5” x 7”, on 380 GSM premium paper` },
        { text: tq`Information card, 4.2” x 5.5,” on 180 GSM Vellum with white ink` },
        { text: tq`Premium almond color envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 5,
    },
    graceTrifold: {
      typeformId: 'QQgHttel',
      shortName: 'Grace Trifold',
      longName: 'Grace Trifold Wedding Invitation Suite',
      costPerHundredString: '$8.81',
      videoUrl: 'https://player.vimeo.com/video/1002122659?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1918423958-cc1c0364f8913c90d260424be616366337c6319be0f4c93c493ad3102085ec38'),
      imageUrl: getInvitationImageUrl('/grace-trifold/grace-trifold-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`All your invitation, RSVP, and wedding details in one arch shape tri-fold ` },
        { text: tq`Printed on our Premium Almond colored paper` },
        { text: tq`This all-in-one wedding invitation includes space for all your wedding day finer details and RSVP information` },
        { text: tq`Almond color envelopes included with each invitation set.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Trifold wedding invitation card, 5” x 16.5” (5” x 7” folded) on 300 GSM almond colored paper` },
        { text: tq`Premium almond color envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 10,
    },
    flutter: {
      typeformId: 'VNo9bohV',
      shortName: 'Flutter',
      longName: 'Flutter Wedding Invitation Suite',
      costPerHundredString: '$13.30',
      videoUrl: 'https://player.vimeo.com/video/1002122888?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1918424244-443431f812254ce2f6b8808bea5253082cd7034e1fcc434121223dba98d2f23d'),
      imageUrl: getInvitationImageUrl('/flutter/flutter-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Vibrant red and blush tones invitation package is perfect for those wanting a pop of color` },
        { text: tq`This set includes a wedding invitation, wedding info card with RSVP details included` },
        { text: tq`An extra card on top with a customizable quote or your names printed on it` },
        { text: tq`Free Premium white envelopes` },
        { text: tq`Gold paperclips included with each set.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5” x 7” on Blush 300 GSM paper` },
        { text: tq`Details and RSVP card, 4.3” x 7” side wave edge on Red 270 GSM paper with white ink` },
        { text: tq`Top card, 3.7” x 7” side wave edge on 300 GSM cotton paper` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        { text: tq`Gold color paper clip` },
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 10,
    },
    koffee: {
      typeformId: 'WUThlXO7',
      shortName: 'Koffee',
      longName: 'Koffee Wedding Invitation Suite',
      costPerHundredString: '$11.59',
      videoUrl: 'https://player.vimeo.com/video/1002123089?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1918424442-802f112f404bfdfdfebcd874365a398b0e2fd3a24df1f15f0228c69a26944cc2'),
      imageUrl: getInvitationImageUrl('/koffee/koffee-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Earthy tone invitation package perfect for those wanting neutral colors` },
        {
          text: tq`This set includes a: Wedding invitation, Wedding timeline (program) card with schedule information, and an information card with all the wedding details and RSVP information`,
        },
        { text: tq`Almond color envelopes included.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5” x 7” on 300 GSM harvest color paper with white ink` },
        { text: tq`Program card,  4.3” x 7” side arch on 300 GSM almond color paper` },
        { text: tq`Info and RSVP card, 3.7” x 5.8” side arch on 300 GSM cotton paper` },
        { text: tq`Premium almond color envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 10,
    },
    grace: {
      typeformId: 'YJkpp1Gs',
      shortName: 'Grace',
      longName: 'Grace Wedding Invitation Suite',
      costPerHundredString: '$14.85',
      videoUrl: 'https://player.vimeo.com/video/1002123486?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1918424894-895a879196f69e937bad2eed2d096867f77fdaae263ed53ef54379fc5d42aae5'),
      imageUrl: getInvitationImageUrl('/grace/grace-1.jpg'),
      descriptionHighlights: highlights(
        {
          text: tq`The ${'Grace'} set is a perfect balance between wedding neutrals and greenery. With stunning new typography and quality print and paper, this layered invitation set is bound to set the tone for your big day.`,
        },
        { text: tq`Matching double-sided information RSVP card ` },
        { text: tq`Small grey name card that can be used as a guest name card and as a place card for your on-the-day stationery` },
        { text: tq`Seedling green envelopes included with each invitation set. ` },
        { text: tq`Each set comes with gold paper clips to keep all your cards together` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5” x 7” arch on premium 300 GSM seedling color paper with white ink` },
        { text: tq`Details card,  3.7” x 5.7” arch on 300 GSM almond color paper` },
        { text: tq`Guest name card,  3.1” x 3.7” half arch on 300 GSM grey color paper with white ink` },
        { text: tq`Premium seedling green color envelopes` },
        sharedEnvelopeAddressing,
        { text: tq`Gold color paper clip` },
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 10,
    },
    roseEtGris: {
      typeformId: 'yoLRmVgW',
      shortName: 'Rose et Gris',
      longName: 'Rose et Gris Wedding Invitation Suite',
      costPerHundredString: '$12.75',
      videoUrl: 'https://player.vimeo.com/video/1001767624?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1918024755-a3e71a0f1a6316b246394a620c45c40c4a5af2fd010ebe0c52cffc7a3a04788b'),
      imageUrl: getInvitationImageUrl('/rose-et-gris/rose-et-gris-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Immerse yourself in the timeless allure of this ${'Rose Et Gris'} invitation set ` },
        { text: tq`Blush RSVP post-card with your address printed on, making it easier for your guests to rsvp` },
        { text: tq`An information card in elegant shade of grey for all the finer details of your big day` },
        { text: tq`A matching foil printed bellyband to tie it all in together ` },
        { text: tq`Free white envelopes included with each invitation set.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5” x 7” on premium 380 GSM paper with gold foil` },
        { text: tq`Details card,  4.2” x 5.5” on 300 GSM grey color paper` },
        { text: tq`RSVP card,  5.5” x 4.2” on 300 GSM blush color paper` },
        { text: tq`Bellyband, 10.5” x 2” on 120 GSM with gold foil` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 10,
    },
    julyTrifold: {
      typeformId: 'tx2gHvcF',
      shortName: 'July Trifold',
      longName: 'July Trifold Wedding Invitation Suite',
      costPerHundredString: '$4.71',
      // TODO: add video and thumbnail once available
      imageUrl: getInvitationImageUrl('/july-trifold/july-trifold-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'July Trifold'} invitation blends soft duck-egg blue artwork and classic typography for a refined, romantic look.` },
        { text: tq`Three-panel, arch-top trifold keeps your invitation, details and RSVP neatly in one all-in-one format.` },
        { text: tq`Printed on 300 gsm Matte Paper for a smooth, luxurious feel and crisp, high-quality reproduction of the design.` },
        { text: tq`Includes a complimentary envelope in your choice of Almond or Desert Blue to complete the suite.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Trifold wedding invitation card, 5" x 16" (5" x 7" folded) on 300 GSM almond colored paper.` },
        { text: tq`Premium almond or desert blue color envelopes.` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 6,
    },
    meetYouAtTheArch: {
      typeformId: 'saMCbgVD',
      shortName: 'Meet You At The Arch',
      longName: 'Meet You At The Arch Wedding Invitation Suite',
      costPerHundredString: '$13.08',
      videoUrl: 'https://player.vimeo.com/video/1145133625?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093982800-5b3fe5fd423baa24acdd8d91ccb3ee1271f64a588cc408fe90e4b14f2f1b672a'),
      imageUrl: getInvitationImageUrl('/meet-you-at-the-arch/meet-you-at-the-arch-1.jpg'),
      descriptionHighlights: highlights(
        {
          text: tq`The ${'Meet You at the Arch'} set is a perfect balance between wedding neutrals and greenery. With stunning new typography and quality print and paper, this layered invitation set is bound to set the tone for your big day.`,
        },
        { text: tq`Matching double-sided information RSVP card` },
        { text: tq`Small Hunter Green name card` },
        { text: tq`Hunter Green envelopes and Delicate Leaves liners included with each invitation set.` },
        { text: tq`Each set comes with gold paper clips to keep all your cards together` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5" x 7" arch on 300 GSM almond paper` },
        { text: tq`Place card, 4.2" x 2.1" landscape arch on Hunter Green 300 GSM paper` },
        { text: tq`RSVP card, 4.2" x 5.5" half arch on Cotton 300 GSM paper` },
        { text: tq`Premium Hunter Green envelopes` },
        { text: tq`Envelope liners` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    delicateLuxe: {
      typeformId: 's3CuGM54',
      shortName: 'Delicate Luxe',
      longName: 'Delicate Luxe Wedding Invitation Suite',
      costPerHundredString: '$11.88',
      videoUrl: 'https://player.vimeo.com/video/1145133607?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093982769-c74f55efdac325010079410ddbe7cdf92832462a487f9a7a82a625948544f77e'),
      imageUrl: getInvitationImageUrl('/delicate-luxe/delicate-luxe-1.jpg'),
      descriptionHighlights: highlights(
        {
          text: tq`The ${'Delicate Luxe'} set is a perfect balance between wedding neutrals and minimalist elegance. With stunning script fonts and quality print and paper, this layered invitation set is bound to set the tone for your big day.`,
        },
        { text: tq`Black information card that is big enough for all your wedding details and sure to stand out` },
        { text: tq`Small round RSVP card for you to place your QR code and any additional details` },
        { text: tq`Grey envelopes with each invitation set` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Wedding invitation card, 5" x 7" arch on 300 GSM cotton paper` },
        { text: tq`Info card, 4.2" x 5.5" half arch on 270 GSM black paper with white ink` },
        { text: tq`RSVP card, 108mm circle on 300 GSM almond paper` },
        { text: tq`Premium grey envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    lavenderLeaves: {
      typeformId: 'JcyCS882',
      shortName: 'Lavender Leaves',
      longName: 'Lavender Leaves Wedding Invitation Suite',
      costPerHundredString: '$4.24',
      videoUrl: 'https://player.vimeo.com/video/1145139142?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991228-84d87ccb81dd8fbc8ad508f03aa25688ce5904e26c2247071c0a593e2bab9a86'),
      imageUrl: getInvitationImageUrl('/lavender-leaves/lavender-leaves-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Lavender Leaves'} gatefold invitation is for those lavender lovers, sure to set the tone for your special day.` },
        { text: tq`Customize with your own photo on the outside` },
        { text: tq`Printed on our matte paper with high quality photo printing` },
        { text: tq`This all-in-one wedding invitation includes all your wedding day finer details` },
        { text: tq`Free white envelopes with each invitation` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    whiteGarden: {
      typeformId: 'yP2Y5eiY',
      shortName: 'White Garden',
      longName: 'White Garden Wedding Invitation Suite',
      costPerHundredString: '$4.24',
      videoUrl: 'https://player.vimeo.com/video/1145139550?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991969-bcc5355c4b091a72f41f839e51e8491f88b622a3154f4dc45bcb3b013a20f6fa'),
      imageUrl: getInvitationImageUrl('/white-garden/white-garden-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'White Garden'} gatefold invitation is for those flower and nature lovers, sure to set the tone for your special day.` },
        { text: tq`Printed on our matte paper with high quality print showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes all your wedding day finer details and RSVP information` },
        { text: tq`Free white envelopes to match for each invitation` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    goldFoilFrame: {
      typeformId: 'sj4b8ewK',
      shortName: 'Gold Foil Frame',
      longName: 'Gold Foil Frame Wedding Invitation Suite',
      costPerHundredString: '$5.03',
      videoUrl: 'https://player.vimeo.com/video/1145139480?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991845-d04ed9e5bdc1da9645cd857a9c0ac0e7e7b3721d48708bc3d82e55bc34029946'),
      imageUrl: getInvitationImageUrl('/gold-foil-frame/gold-foil-frame-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Gold Foil Frame'} gatefold invitation is an elegant foil design, sure to set the tone for your special day.` },
        { text: tq`Printed on our matte paper with high quality print showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes space for all your wedding day detail, RSVP and wedding day timeline information.` },
        { text: tq`Black envelopes included with each invitation set` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper with gold foil` },
        { text: tq`Premium black envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    gardenWindow: {
      typeformId: 'bdgNwm7b',
      shortName: 'Garden Window',
      longName: 'Garden Window Wedding Invitation Suite',
      costPerHundredString: '$4.24',
      videoUrl: 'https://player.vimeo.com/video/1145139426?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991705-0a980ba3f16cc77d95ca8990df8c4d9848fb4361f90cf4c58bda144831e9c8c6'),
      imageUrl: getInvitationImageUrl('/garden-window/garden-window-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Garden Window'} gatefold invitation is for those nature lovers, sure to set the tone for your special day.` },
        { text: tq`Printed on our matte paper with high quality print showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes space for all your wedding day detail, RSVP and wedding day timeline information.` },
        { text: tq`Free white envelopes included with each invitation set` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    loveAndLeaves: {
      typeformId: 'SF4oWr62',
      shortName: 'Love and Leaves',
      longName: 'Love and Leaves Wedding Invitation Suite',
      costPerHundredString: '$4.24',
      videoUrl: 'https://player.vimeo.com/video/1145139341?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991571-1e7d5b1e396e9983e380d81691f0fc87316778341d00a72cc17a3de671c54b6c'),
      imageUrl: getInvitationImageUrl('/love-and-leaves/love-and-leaves-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Love and Leaves'} gatefold invitation is for those nature lovers, sure to set the tone for your special day.` },
        { text: tq`Printed on our matte paper with high quality print showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes space for all your wedding day detail, RSVP and wedding day timeline information.` },
        { text: tq`Free white envelopes included with each invitation set` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    lunaGatefold: {
      typeformId: 'P4DOn574',
      shortName: 'Luna Gatefold',
      longName: 'Luna Gatefold Wedding Invitation Suite',
      costPerHundredString: '$5.28',
      videoUrl: 'https://player.vimeo.com/video/1145139307?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991528-16c25eaa0fd019102b89ab229fdc6a20c45e08e289d2d197f79bfcbd9cc99f61'),
      imageUrl: getInvitationImageUrl('/luna-gatefold/luna-gatefold-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Luna Gatefold'} gatefold invitation is for those nature lovers, sure to set the tone for your special day.` },
        { text: tq`Printed on our matte paper with high quality print showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes space for all your wedding day detail, RSVP and wedding day timeline information.` },
        { text: tq`Free Almond envelopes included with each invitation set` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper` },
        { text: tq`Premium almond color envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    eucalyptus: {
      typeformId: 'Y3H41GrA',
      shortName: 'Eucalyptus',
      longName: 'Eucalyptus Wedding Invitation Suite',
      costPerHundredString: '$4.24',
      videoUrl: 'https://player.vimeo.com/video/1145139379?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991616-3e1f0acb5756a8dea1b71f51ec2cb02bad1fee00c8ec18f6c07363746ac7a6ea'),
      imageUrl: getInvitationImageUrl('/eucalyptus/eucalyptus-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Eucalyptus'} gatefold invitation is for those nature lovers, sure to set the tone for your special day.` },
        { text: tq`Printed on our matte paper with high quality print showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes space for all your wedding day detail, RSVP and wedding day timeline information.` },
        { text: tq`Free white envelopes included with each invitation set` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    graphiteBlooms: {
      typeformId: 'cS9aFSYM',
      shortName: 'Graphite Blooms',
      longName: 'Graphite Blooms Wedding Invitation Suite',
      costPerHundredString: '$5.02',
      videoUrl: 'https://player.vimeo.com/video/1145139515?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991891-32e77e3798ad95f0c0d6564b925a34570715508b8caa6e4a545ccfc93e7cd205'),
      imageUrl: getInvitationImageUrl('/graphite-blooms/graphite-blooms-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Graphite Blooms'} gatefold invitation is for those fine art lovers, sure to set the tone for your special day.` },
        { text: tq`Printed on our matte paper with high quality print showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes space for all your wedding day detail, RSVP and wedding day timeline information.` },
        { text: tq`Free Black envelopes included with each invitation set` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper` },
        { text: tq`Premium black envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    modernSimplicity: {
      typeformId: 'KxfpCx2r',
      shortName: 'Modern Simplicity',
      longName: 'Modern Simplicity Wedding Invitation Suite',
      costPerHundredString: '$5.29',
      videoUrl: 'https://player.vimeo.com/video/1145139465?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991776-5ca2fa26c442808e316141cc79257ce5cacba1ebd2b1d710b6414fad9f38fb87'),
      imageUrl: getInvitationImageUrl('/modern-simplicity/modern-simplicity-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Modern Simplicity'} gatefold invitation is an elegant foil design, sure to set the tone for your special day.` },
        { text: tq`Printed on our matte paper with high quality print showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes space for all your wedding day detail, RSVP and wedding day timeline information.` },
        { text: tq`Almond colour envelopes included with each invitation set` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper with rose gold foil` },
        { text: tq`Premium almond color envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    ivoryGarden: {
      typeformId: 'v8eD9B3Z',
      shortName: 'Ivory Garden',
      longName: 'Ivory Garden Wedding Invitation Suite',
      costPerHundredString: '$5.28',
      videoUrl: 'https://player.vimeo.com/video/1145139402?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991658-a0cbb7270e32ca4d9abdeae64a4bf5c3e286119b7237ca813fb4735038888321'),
      imageUrl: getInvitationImageUrl('/ivory-garden/ivory-garden-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Ivory Garden'} gatefold invitation is for those flower and nature lovers, sure to set the tone for your special day.` },
        { text: tq`Printed on our matte paper with high quality print showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes all your wedding day finer details and RSVP information` },
        { text: tq`Free Almond envelopes to match for each invitation` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper` },
        { text: tq`Premium almond color envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
    gardenEstate: {
      typeformId: 'jCoxpiWM',
      shortName: 'Garden Estate',
      longName: 'Garden Estate Wedding Invitation Suite',
      costPerHundredString: '$5.29',
      videoUrl: 'https://player.vimeo.com/video/1145139278?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991879-1e31a62f89f011256570da45892ce37784a245edc45ea0399331989c8bb24d64'),
      imageUrl: getInvitationImageUrl('/garden-estate/garden-estate-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Garden Estate'} gatefold invitation is for those nature lovers, sure to set the tone for your special day.` },
        { text: tq`Printed on our matte paper with a combination of digital and copper color foil, showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes space for all your wedding day detail, RSVP and wedding day timeline information.` },
        { text: tq`Free Forest Green envelopes included with each invitation set` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper with copper foil` },
        { text: tq`Premium Forest Green envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 10,
    },
    botanicalGeo: {
      typeformId: 't2rUqRcD',
      shortName: 'Botanical Geo',
      longName: 'Botanical Geo Wedding Invitation Suite',
      costPerHundredString: '$5.28',
      videoUrl: 'https://player.vimeo.com/video/1145139247?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('2093991445-5b864c4859f0171fb37868d840fef52878ca424722c3692fd4c4cde04ab8f4cf'),
      imageUrl: getInvitationImageUrl('/botanical-geo/botanical-geo-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Botanical Geo'} gatefold invitation is for those nature lovers, sure to set the tone for your special day.` },
        { text: tq`Printed on our matte paper with high quality print showing all the vibrant colors this design has to offer` },
        { text: tq`This all-in-one wedding invitation includes space for all your wedding day detail, RSVP and wedding day timeline information.` },
        { text: tq`Free Seedling green envelopes included with each invitation set` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Gatefold wedding invitation card, 10" x 7" on 300 GSM matte paper` },
        { text: tq`Premium seedling green color envelopes` },
        sharedEnvelopeAddressing,
      ),
      printingBusinessDaysQuantity: 5,
    },
  } as const);

  return invitationOfferings;
};

/**
 * A wrapper around `createProductOfferings` that creates saveTheDate offerings
 * This helps us to properly type `PriceConfig` in `src/apps/card/routes/Concierge/config.ts`
 * @param tq - The translation function
 * @returns The invitation offerings
 */
export const createSaveTheDateOfferingsData = (tq: QuickTranslate) => {
  const { sharedEnvelopeAddressing, sharedNotIncluded, sharedPersonalization } = getSharedHighlights(tq);

  const saveTheDateOfferingsInput = createProductOfferings({
    byron: {
      typeformId: `sPCMvsfe`,
      shortName: 'Byron',
      longName: 'Byron Save the Date',
      costPerHundredString: '$3.22',
      videoUrl: 'https://player.vimeo.com/video/1006745499?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1923505068-b3897f91cf25802ae46805cb1a9bcc8d129f69f3d4603a208e96bb76cb741216'),
      imageUrl: getSaveTheDateImageUrl('/byron/byron-1.jpg'),
      descriptionHighlights: highlights(
        {
          text: tq`Inspired by the ${'Byron'} shorelines in Australia, this arch shaped copper foil Save the Date is the perfect way to set the tone for your big day.`,
        },
        { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Save the Date card, 4.2” x 5.5” arch on luxe 380 GSM paper with copper foil` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 6,
    },
    clairDeLune: {
      typeformId: `Ke14NH3i`,
      shortName: 'Clair De Lune',
      longName: 'Clair De Lune Save the Date',
      costPerHundredString: '$3.27',
      videoUrl: 'https://player.vimeo.com/video/1006745058?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1923504604-6e59b3bf477e599bd64165ac6c2cce872523e6ab8027b2cbceeb0e19d2d36037'),
      imageUrl: getSaveTheDateImageUrl('/clairDeLune/clair_de_lune-3.jpg'),
      descriptionHighlights: highlights(
        {
          text: tq`The ${'Clair De Lune'} double-layer Save the Dates are a delightful blend of playfulness and elegance. The top layer, printed in white ink on delicate vellum, overlays your personalized photo on the second layer, creating a beautiful keepsake.`,
        },
        { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Save the Date card, 4.2” x 5.5” on 350 GSM heavyweight paper` },
        { text: tq`Sheer overlay, 4.2” x 5.5” on 180 GSM vellum with white ink` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        { text: tq`Hole punch (ribbon not included)` },
        { text: tq`Gold fastener` },
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 2,
    },
    delilah: {
      typeformId: `Tes3A0DI`,
      shortName: 'Delilah',
      longName: 'Delilah Save the Date',
      costPerHundredString: '$2.71',
      videoUrl: 'https://player.vimeo.com/video/1006745036?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1923504570-0f3e7d691b23445a1d691f36e5c9e9db419fa7b90392f0af1bb17cb53970d78c'),
      imageUrl: getSaveTheDateImageUrl('/delilah/delilah-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Where rustic charm meets minimalism, the ${'Delilah'} arch-shaped save the dates perfectly set the tone for your special day.` },
        { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Save the Date card, 4.2” x 5.5” arch on 300 GSM cotton paper` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 6,
    },
    floweret: {
      typeformId: `q3pZSUDr`,
      shortName: 'Floweret',
      longName: 'Floweret Save the Date',
      costPerHundredString: '$2.71',
      videoUrl: 'https://player.vimeo.com/video/1006745029?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1923504483-d0831c1ac8d498058527e3674a3cd87e5e38b2d68ca59abcefda273d1bc9dace'),
      imageUrl: getSaveTheDateImageUrl('/floweret/floweret-1.jpg'),
      descriptionHighlights: highlights(
        {
          text: tq`The double-arch ${'Floweret'} Save the Date features a graceful blend of blossom prints and typewriter-style fonts. It’s a charming way to announce your special day with a touch of timeless elegance.`,
        },
        { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Save the Date card, 4.2” x 5.5” double arch on 300 GSM cotton paper` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 6,
    },
    linear: {
      typeformId: `jWmW0z53`,
      shortName: 'Linear',
      longName: 'Linear Save the Date',
      costPerHundredString: '$3.68',
      videoUrl: 'https://player.vimeo.com/video/1006745515?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1923505088-f3792b40df36dcf7a65e897aec116a73fe169d0f99c98696e83c24d4521faafa'),
      imageUrl: getSaveTheDateImageUrl('/linear/linear-1.jpg'),
      descriptionHighlights: highlights(
        {
          text: tq`Modern typography meets premium cotton paper. The ${'Linear'} is all about simplicity and elegance to set the tone for your big day.`,
        },
        { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Save the Date card, 4.2” x 5.5” on 300 GSM cotton paper` },
        { text: tq`Premium almond envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 2,
    },
    luna: {
      typeformId: `Zg9kqxhL`,
      shortName: 'Luna',
      longName: 'Luna Save the Date',
      costPerHundredString: '$3.03',
      videoUrl: 'https://player.vimeo.com/video/1006745499?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1923505068-b3897f91cf25802ae46805cb1a9bcc8d129f69f3d4603a208e96bb76cb741216'),
      imageUrl: getSaveTheDateImageUrl('/luna/luna-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`A blend of gold foil and digital printing, this arch-shaped Save the Date is perfect for setting the tone of your special day. ` },
        { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Save the Date card, 4.2” x 5.5” arch on luxe 380 GSM paper with gold foil` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 6,
    },
    minimalisme: {
      typeformId: `Sg0zZ4gq`,
      shortName: 'Minimalisme',
      longName: 'Minimalisme Save the Date',
      costPerHundredString: '$3.79',
      videoUrl: 'https://player.vimeo.com/video/1006745485?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1923505048-4d254e0268e9772b6ef310131906f0887f52642d986b50a1040a9155fecef66b'),
      imageUrl: getSaveTheDateImageUrl('/minimalisme/minimalisme-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Personalized photo cards are the perfect way to announce your big day.` },
        { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Save the Date card, 4.2” x 5.5” arch on luxe 380 GSM paper` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 6,
    },
    minimalistBlackWhite: {
      typeformId: `TazsjsAH`,
      shortName: 'Minimalist Black & White',
      longName: 'Minimalist Black & White Save the Date',
      costPerHundredString: '$3.14',
      videoUrl: 'https://player.vimeo.com/video/1006745470?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1923505038-5371188bd63a947e043838c4cfb959246b37092285e6748a634fee9575d8ebad'),
      imageUrl: getSaveTheDateImageUrl('/minimalistBlackWhite/minimalistBlackWhite-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`This Foil and personalized photo combo is the perfect way to send out your Save the Dates in style.` },
        { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Save the Date card, 4.2” x 5.5” on 350 GSM heavyweight paper with gold foil` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 6,
    },
    polaroid: {
      typeformId: `KgMpzGl8`,
      shortName: 'Polaroid',
      longName: 'Polaroid Save the Date',
      costPerHundredString: '$2.26',
      videoUrl: 'https://player.vimeo.com/video/1006745454?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1923505179-d7acabb7175f59d296b9ab8f2d55850b13325edbe75c7fb8ddbfa26ebc10cad4'),
      imageUrl: getSaveTheDateImageUrl('/polaroid/polaroid-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`Announce your big day with your own photo captured on premium stock, creating a keepsake that lasts.` },
        { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Save the Date card, 4.2” x 5.5” on luxe 380 GSM paper` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 2,
    },
    summerMontage: {
      typeformId: `Ime15gVQ`,
      shortName: 'Summer Montage',
      longName: 'Summer Montage Save the Date',
      costPerHundredString: '$3.11',
      videoUrl: 'https://player.vimeo.com/video/1006748982?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1923509108-65dde415c0809e2f4bdd975b91e3895859f125f45e03a3e77e8ba3484528984f'),
      imageUrl: getSaveTheDateImageUrl('/summerMontage/summerMontage-1.jpg'),
      descriptionHighlights: highlights(
        { text: tq`The ${'Summer Montage'} double-arch Save the Date is the perfect way to announce your big day, personalized with your very own photo.` },
        { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Save the Date card, 4.2” x 5.5” double arch on luxe 380 GSM paper with white ink` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 6,
    },
    summerNights: {
      typeformId: `W0MJZcgL`,
      shortName: 'Summer Nights',
      longName: 'Summer Nights Save the Date',
      costPerHundredString: '$3.11',
      videoUrl: 'https://player.vimeo.com/video/1006745437?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
      videoThumbnailUrl: getVideoThumbnailUrl('1923505003-181970c8e4225285b9451234dac82ed661e32e9e3d7ca492ff24060064420b00'),
      imageUrl: getSaveTheDateImageUrl('/summerNights/summerNights-1.jpg'),
      descriptionHighlights: highlights(
        {
          text: tq`${'Summer Nights'} Save the Dates are a perfect blend of blue and orange hues, a masterpiece crafted for those who dream of warm sands and the whisper of waves.`,
        },
        { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
      ),
      suiteHighlights: highlights(
        sharedPersonalization,
        { text: tq`Save the Date card, 4.2” x 5.5” arch on luxe 380 GSM paper` },
        { text: tq`Premium white envelopes` },
        sharedEnvelopeAddressing,
        sharedNotIncluded,
      ),
      printingBusinessDaysQuantity: 6,
    },
  } as const);

  return saveTheDateOfferingsInput;
};

// Enforce no overlapping keys
type OverlappingKeys = keyof ReturnType<typeof createInvitationOfferingsData> & keyof ReturnType<typeof createSaveTheDateOfferingsData>;
type NoOverlappingKeys = OverlappingKeys extends never ? true : never;
const _assert: NoOverlappingKeys = true;
void _assert;
